import { useEffect, useState } from "react";
import { ReactComponent as ViewMoreIcon } from "../../../Assets/Icons/ViewMoreIcon.svg";
import GiftCardProgressBar from "../common/GiftCardProgressBar";
import { GiftCardsButton } from "../../common/GiftCardsButton";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getGiftCardById } from "../../../services/giftCardService";
import { setCard, setAmount } from "../../../redux/slices/giftCardCheckoutSlice";
import toast from "../../../utils/toast";
import { makeLinksClickable } from "../../../utils/linkClickable";

const GiftCardSelectPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedAmount, setSelectedAmount] = useState(null);
  const [showMoreTerms, setShowMoreTerms] = useState(false);
  const [error, setError] = useState("");
const params = useParams();
  const { giftCard, loading } = useSelector((state) => state.giftCardReducer);

  useEffect(() => {
    if (id) dispatch(getGiftCardById(id));
  }, [id, dispatch]);

  const min = giftCard?.valueRestrictions?.minimum || 10;
  const max = giftCard?.valueRestrictions?.maximum || 100;
  const mid = Math.round((min + max) / 2);

  // Auto-set default mid value
  // useEffect(() => {
  //   if (!selectedAmount && giftCard) setSelectedAmount(mid.toString());
  // }, [mid, giftCard, selectedAmount]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-gray-500">Loading gift card details...</p>
      </div>
    );
  }

  if (!giftCard) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-gray-500">Gift card not found</p>
      </div>
    );
  }

  const handleContinue = () => {
    if (!selectedAmount) {
      toast?.error?.("Please select an amount");
      return;
    }
    dispatch(setCard(giftCard));
    dispatch(setAmount(Number(selectedAmount)));
       // navigate(`/gift-card/enter-details/${id}`);
    navigate(`/gift-card/confirm-pay/${params.id}`);
  };

  return (
    <div className="max-w-4xl mx-auto bg-white min-h-screen p-4">
      <GiftCardProgressBar
        steps={["Select", "Confirm & Pay", "Thank You"]}
        currentStep={0}
      />

      <div className="py-6 pb-[120px]">
        <div className="bg-black rounded-lg mb-4 flex justify-center items-center">
          {giftCard?.productImage ? (
            <img
              className="w-full max-h-[50vh] rounded-lg"
              src={giftCard.productImage}
              alt={giftCard.productName}
            />
          ) : (
            <div className="w-full h-[25vh] flex items-center justify-center bg-gray-200 text-gray-500">
              Image not found
            </div>
          )}
        </div>

        <h1 className="text-md font-semibold text-center mb-4">
          {giftCard.productName || "Gift Card"}
        </h1>
        <div className="mb-4">
          <h2 className="font-semibold text-sm mb-3">Terms & conditions:</h2>
          {!showMoreTerms && (
            <ul className="text-[#8C8C8C] text-[12px] space-y-2 list-disc list-inside">
              {giftCard?.termsAndConditions?.text
                ?.replace(/<[^>]+>/g, " ")
                ?.split(/(?<=\.)\s+/)
                .filter((line) => line.trim() !== "")
                .slice(0, 2)
                .map((line, index) => (
                  <li
                    key={index}
                    className="break-words [&_a]:break-all [&_a]:text-[#00ADEF] [&_a]:underline"
                  >
                    {makeLinksClickable(line.trim())}
                  </li>
                ))}
            </ul>
          )}
          <button
            onClick={() => setShowMoreTerms(!showMoreTerms)}
            className="flex items-center text-[12px] font-medium mt-3 hover:text-gray-700"
          >
            {showMoreTerms ? "View Less" : "View More"}
            <ViewMoreIcon
              className={`w-4 h-4 ml-1 transition-transform ${
                showMoreTerms ? "rotate-90" : ""
              }`}
            />
          </button>
          {showMoreTerms && (
            <ul className="mt-3 text-[#8C8C8C] text-[12px] space-y-2 list-disc list-inside">
              {giftCard?.termsAndConditions?.text
                ?.replace(/<[^>]+>/g, " ")
                ?.split(/(?<=\.)\s+/)
                .filter((line) => line.trim() !== "")
                .map((line, index) => (
                  <li
                    key={index}
                    className="break-words [&_a]:break-all [&_a]:text-[#00ADEF] [&_a]:underline"
                  >
                    {makeLinksClickable(line.trim())}
                  </li>
                ))}
            </ul>
          )}
        </div>
        <div className="mb-8">
          <h2 className="font-semibold text-sm mb-3">
            {giftCard.parentBrandName}’s eGift Cards Amount
          </h2>

          <div className="bg-[#F2F4F5] p-4 rounded-lg">
            <h3 className="text-sm font-semibold mb-3 text-left">Enter Amount</h3>

            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => {
                  if (!selectedAmount) return;
                  const newValue = Number(selectedAmount) - 1;
                  if (isNaN(newValue)) return;
                  if (newValue < min) {
                    setError(`Minimum value allowed is $${min}`);
                    return;
                  }
                  setError("");
                  setSelectedAmount(newValue.toString());
                }}
                disabled={!selectedAmount}
                className={`bg-[#00ADEF] shadow-sm border border-gray-200 rounded-full w-10 h-10 flex items-center justify-center text-4xl text-white active:scale-95 transition-transform hover:bg-[#00ADEF] ${
                  !selectedAmount ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                –
              </button>
              <div className="flex items-center justify-center border border-gray-300 rounded-lg px-3 py-2 bg-white w-32">
                <span className="text-black font-medium mr-1">$</span>
                <input
                  type="number"
                  step="1"
                  inputMode="numeric"
                  className="w-full text-center outline-none text-gray-800 font-semibold text-base"
                  value={selectedAmount ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedAmount(value);

                    const num = Number(value);
                    if (isNaN(num)) {
                      setError("Invalid number");
                    } else if (num < min) {
                      setError(`Minimum value allowed is $${min}`);
                    } else if (num > max) {
                      setError(`Maximum value allowed is $${max}`);
                    } else {
                      setError("");
                    }
                  }}
                  placeholder="100"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!selectedAmount) return;
                  const newValue = Number(selectedAmount) + 1;
                  if (isNaN(newValue)) return;
                  if (newValue > max) {
                    setError(`Maximum value allowed is $${max}`);
                    return;
                  }
                  setError("");
                  setSelectedAmount(newValue.toString());
                }}
                disabled={!selectedAmount}
                className={`bg-[#00ADEF] shadow-sm border border-gray-200 rounded-full w-10 h-10 flex items-center justify-center text-4xl text-white active:scale-95 transition-transform hover:bg-[#00ADEF] ${
                  !selectedAmount ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                +
              </button>
            </div>
            {error && (
              <p className="text-[13px] text-red-500 mt-3 text-left font-medium">
                {error}
              </p>
            )}
          </div>
        </div>
        <div className="bg-white flex items-center mx-auto w-full max-w-4xl justify-center fixed bottom-0 left-0 right-0 p-4 overflow-hidden border-t-[1px] border-[#F2F4F5]">
          <button
            onClick={handleContinue}
            disabled={
              !selectedAmount ||
              error ||
              Number(selectedAmount) < min ||
              Number(selectedAmount) > max
            }
            className={`border-2 border-[#00ADEF] w-full px-6 py-3 rounded-[8px] font-[600] text-[#FFFFFF] text-center font-poppins transition-opacity ${
              !selectedAmount ||
              error ||
              Number(selectedAmount) < min ||
              Number(selectedAmount) > max
                ? "bg-[#00ADEF] cursor-not-allowed"
                : "bg-[#00ADEF] hover:opacity-60"
            }`}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default GiftCardSelectPage;


